// src/utils/stylesApplier.ts
import type {
    RestaurantConfig,
    ThemePalette,
    StyleValue,
    FontSizeReference,
    FontsConfig,
    Theme,
    TokenReference,
} from "../ui/uiConfig";

const COLOR_TOKENS = ["primary", "secondary", "brand1", "brand2", "brand3", "brand4", "brand5"] as const;

const FONT_SIZE_MAP: Record<FontSizeReference, string> = {
    sizePrimary: "--font-size-primary",
    sizeSecondary: "--font-size-secondary",
    sizeTertiary: "--font-size-tertiary",
    sizeXS: "--font-size-xs",
};

const toPx = (v: any, fallback: string) => {
    if (v == null || v === "") return fallback;
    if (typeof v === "number") return `${v}px`;

    const s = String(v).trim();
    if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`;

    return s;
};




const resolveColor = (config: RestaurantConfig, colorRef: string, theme: Theme): string => {
    if (colorRef === "transparent" || colorRef === "inherit") return colorRef;

    const palette = config.colors?.themes?.[theme] as ThemePalette | undefined;
    return (palette?.[colorRef as keyof ThemePalette] as string) ?? colorRef;
};

const resolveFontFamily = (config: RestaurantConfig, ref: "primary" | "secondary" | "tertiary"): string => {
    const font = (config.fonts as any)?.[ref]?.name;
    return font ? `var(--font-family-${ref})` : "sans-serif";
};

const resolveToken = (config: RestaurantConfig, tokenRef: TokenReference): string => {
    const [group, key] = tokenRef.split(".") as [string, string];

    if (group === "radius") {
        const n = (config.tokens?.radius as any)?.[key];
        return typeof n === "number" ? `${n}px` : tokenRef;
    }
    if (group === "space") {
        const n = (config.tokens?.space as any)?.[key];
        return typeof n === "number" ? `${n}px` : tokenRef;
    }
    if (group === "shadow") {
        const s = (config.tokens?.shadow as any)?.[key];
        return typeof s === "string" ? s : tokenRef;
    }

    return tokenRef;
};

/**
 * ÚNICO lugar donde se traduce token → CSS value (v2)
 */
const resolveStyleValue = (value: StyleValue, config: RestaurantConfig, theme: Theme): string => {
    if (value == null || value === "") return "";

    // Tokens de color
    if (COLOR_TOKENS.includes(value as any)) {
        return resolveColor(config, value as string, theme);
    }

    // Tamaños de fuente por token (sizePrimary, etc.)
    if (typeof value === "string" && value in FONT_SIZE_MAP) {
        return `var(${FONT_SIZE_MAP[value as FontSizeReference]})`;
    }

    // Tokens (radius.md, space.lg, shadow.sm)
    if (typeof value === "string" && /^(radius|space|shadow)\./.test(value)) {
        return resolveToken(config, value as TokenReference);
    }

    // Font family (fontPrimary, fontSecondary, fontTertiary)
    if (typeof value === "string" && value.startsWith("font")) {
        const ref = value.replace("font", "").toLowerCase() as any; // primary|secondary|tertiary
        return resolveFontFamily(config, ref);
    }

    // Números → px
    if (typeof value === "number") return `${value}px`;

    return String(value);
};

const applySpecificStyles = (
    root: HTMLElement,
    config: RestaurantConfig,
    styles: Record<string, StyleValue> | undefined,
    prefix: string,
    theme: Theme
) => {
    for (const [key, value] of Object.entries(styles || {})) {
        root.style.setProperty(`--${prefix}-${key}`, resolveStyleValue(value, config, theme));
    }
};

export const applyRestaurantStyles = (config: RestaurantConfig, theme: Theme) => {
    const root = document.documentElement;

    // 1) Colores globales (para var(--color-*))
    const palette = config.colors?.themes?.[theme];
    root.style.setProperty("--color-primary", palette?.primary ?? "");
    root.style.setProperty("--color-secondary", palette?.secondary ?? "");
    root.style.setProperty("--color-brand1", palette?.brand1 ?? "");
    root.style.setProperty("--color-brand2", palette?.brand2 ?? "");
    root.style.setProperty("--color-brand3", palette?.brand3 ?? "");
    root.style.setProperty("--color-brand4", palette?.brand4 ?? "");
    root.style.setProperty("--color-brand5", palette?.brand5 ?? "");
    // 2) Fonts globales
    const fonts = config.fonts as FontsConfig;
    const primaryFont = (fonts as any)?.primary?.name || "sans-serif";

    root.style.setProperty("--font-family-primary", primaryFont);
    root.style.setProperty("--font-family-secondary", (fonts as any)?.secondary?.name || primaryFont);
    root.style.setProperty("--font-family-tertiary", (fonts as any)?.tertiary?.name || primaryFont);

    // 3) Font sizes (px si vienen number)
    root.style.setProperty("--font-size-primary", toPx((fonts as any)?.sizePrimary, "16px"));
    root.style.setProperty("--font-size-secondary", toPx((fonts as any)?.sizeSecondary, "14px"));
    root.style.setProperty("--font-size-tertiary", toPx((fonts as any)?.sizeTertiary, "12px"));
    root.style.setProperty("--font-size-xs", toPx((fonts as any)?.sizeXS, "10px"));

    // 4) Tokens globales -> CSS vars globales
    const tokens = config.tokens ?? {};
    const space = tokens.space ?? {};
    const radius = tokens.radius ?? {};
    const shadow = tokens.shadow ?? {};

    for (const [k, v] of Object.entries(space)) {
        if (typeof v === "number") root.style.setProperty(`--space-${k}`, `${v}px`);
    }
    for (const [k, v] of Object.entries(radius)) {
        if (typeof v === "number") root.style.setProperty(`--radius-${k}`, `${v}px`);
    }
    for (const [k, v] of Object.entries(shadow)) {
        if (typeof v === "string") root.style.setProperty(`--shadow-${k}`, v);
    }

    // 5) Dimensiones útiles (navbar heights)
    const nav = config.components?.navbar;
    root.style.setProperty("--navbar-height-mobile", `${nav?.heightMobile ?? 0}px`);
    root.style.setProperty("--navbar-height-desktop", `${nav?.heightDesktop ?? 0}px`);

    // 6) UI defaults (v2) -> CSS vars (resolviendo tokens/colores)
    if (config.ui?.card) {
        root.style.setProperty("--ui-card-borderRadius", resolveStyleValue(config.ui.card.borderRadius as any, config, theme));
        root.style.setProperty("--ui-card-shadow", resolveStyleValue(config.ui.card.shadow as any, config, theme));
    }

    if (config.ui?.button) {
        root.style.setProperty("--ui-button-borderRadius", resolveStyleValue(config.ui.button.borderRadius as any, config, theme));
        root.style.setProperty("--ui-button-paddingX", resolveStyleValue(config.ui.button.paddingX ?? null, config, theme));
        root.style.setProperty("--ui-button-paddingY", resolveStyleValue(config.ui.button.paddingY ?? null, config, theme));
        root.style.setProperty("--ui-button-fontSize", resolveStyleValue(config.ui.button.fontSize as any, config, theme));
    }

    if (config.ui?.input) {
        root.style.setProperty("--ui-input-borderRadius", resolveStyleValue(config.ui.input.borderRadius as any, config, theme));
        root.style.setProperty("--ui-input-padding", config.ui.input.padding ?? "");

        root.style.setProperty("--ui-input-backgroundColor", resolveStyleValue(config.ui.input.backgroundColor as any, config, theme));
        root.style.setProperty("--ui-input-textColor", resolveStyleValue(config.ui.input.textColor as any, config, theme));
        root.style.setProperty("--ui-input-placeholderColor", resolveStyleValue(config.ui.input.placeholderColor as any, config, theme));
        root.style.setProperty("--ui-input-borderColor", resolveStyleValue(config.ui.input.borderColor as any, config, theme));
        root.style.setProperty("--ui-input-focusBorderColor", resolveStyleValue(config.ui.input.focusBorderColor as any, config, theme));
    }

    if (config.ui?.modal) {
        root.style.setProperty("--ui-modal-padding", resolveStyleValue(config.ui.modal.padding as any, config, theme));
        root.style.setProperty("--ui-modal-shadow", resolveStyleValue(config.ui.modal.shadow as any, config, theme));
        root.style.setProperty("--ui-modal-overlayColor", config.ui.modal.overlayColor ?? "");
    }

    // 7) Components v2 -> CSS vars por cssPrefix
    const components = config.components ?? {};
    for (const comp of Object.values(components)) {
        const prefix = (comp as any)?.cssPrefix;
        const styles = (comp as any)?.styles;
        if (prefix && styles) {
            applySpecificStyles(root, config, styles, prefix, theme);
        }
    }

    // al final de applyRestaurantStyles()
    const faviconHref = (config as any).favicon as string | undefined;
    if (faviconHref) {
        const link =
            (document.getElementById("favicon") as HTMLLinkElement | null) ??
            (document.querySelector('link[rel~="icon"]') as HTMLLinkElement | null);

        if (link) {
            link.type = "image/webp";
            link.href = `${faviconHref}?v=${config.imagesVersion || Date.now()}`;
        }
    }

};